/**
 * ===============================================================================
 * MEGA MENU - PROFESSIONAL EDITION - JAVASCRIPT
 * ===============================================================================
 * Enhanced megamenu interactions with smooth animations
 * ===============================================================================
 */

class MegaMenuProfessional {
  constructor() {
    this.megaMenus = document.querySelectorAll('.mega-menu--professional');
    this.init();
    this.fixPositioning();
  }

  init() {
    this.megaMenus.forEach((menu) => {
      this.setupAnimations(menu);
      this.setupAccessibility(menu);
      this.setupHoverEffects(menu);
    });
  }

  /**
   * Fix positioning to make all menus start from viewport left edge (pixel 0)
   */
  fixPositioning() {
    const disclosures = document.querySelectorAll('mega-menu-disclosure');
    
    disclosures.forEach((disclosure) => {
      const details = disclosure.querySelector('details');
      const menu = disclosure.querySelector('.mega-menu--professional');
      
      if (!details || !menu) return;

      const adjustPosition = () => {
        // Get viewport width (excluding scrollbar)
        const viewportWidth = document.documentElement.clientWidth;
        
        // Get the parent nav item position
        const navItem = disclosure.closest('.header__primary-nav-item');
        if (!navItem) return;
        
        const navItemRect = navItem.getBoundingClientRect();
        
        // Calculate how far the nav item is from the left edge of viewport
        const distanceFromLeft = navItemRect.left;
        
        // Set menu to start at viewport left edge by offsetting negative distance
        menu.style.left = `-${distanceFromLeft}px`;
        menu.style.width = `${viewportWidth}px`;
        menu.style.maxWidth = `${viewportWidth}px`;
        menu.style.right = 'auto';
        menu.style.transform = 'none';
      };

      // Adjust when menu opens
      details.addEventListener('toggle', () => {
        if (details.open) {
          // Immediate adjustment
          adjustPosition();
          // Second adjustment after render
          requestAnimationFrame(() => {
            adjustPosition();
          });
        }
      });

      // Adjust on window resize
      let resizeTimeout;
      window.addEventListener('resize', () => {
        if (details.open) {
          clearTimeout(resizeTimeout);
          resizeTimeout = setTimeout(adjustPosition, 50);
        }
      });
      
      // Adjust on scroll for sticky headers
      let scrollTimeout;
      window.addEventListener('scroll', () => {
        if (details.open) {
          clearTimeout(scrollTimeout);
          scrollTimeout = setTimeout(adjustPosition, 50);
        }
      }, { passive: true });
    });
  }

  /**
   * Setup staggered entrance animations
   */
  setupAnimations(menu) {
    const columns = menu.querySelectorAll('.mega-menu__column');
    const promoCards = menu.querySelectorAll('.mega-menu-promo__card');

    // Mark as visible after animation completes
    setTimeout(() => {
      menu.classList.add('is-visible');
    }, 500);

    // Animate columns with stagger
    columns.forEach((column, index) => {
      column.style.opacity = '0';
      column.style.transform = 'translateY(10px)';
      
      setTimeout(() => {
        column.style.transition = 'opacity 0.4s ease, transform 0.4s cubic-bezier(0.4, 0, 0.2, 1)';
        column.style.opacity = '1';
        column.style.transform = 'translateY(0)';
      }, index * 50);
    });

    // Animate promo cards
    promoCards.forEach((card, index) => {
      card.style.opacity = '0';
      card.style.transform = 'translateY(10px)';
      
      setTimeout(() => {
        card.style.transition = 'opacity 0.4s ease, transform 0.4s cubic-bezier(0.4, 0, 0.2, 1)';
        card.style.opacity = '1';
        card.style.transform = 'translateY(0)';
      }, (columns.length * 50) + (index * 100));
    });
  }

  /**
   * Setup accessibility features
   */
  setupAccessibility(menu) {
    const links = menu.querySelectorAll('a');
    
    links.forEach((link) => {
      // Add keyboard navigation
      link.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          link.click();
        }
      });

      // Add focus styles
      link.addEventListener('focus', () => {
        link.setAttribute('data-focused', 'true');
      });

      link.addEventListener('blur', () => {
        link.removeAttribute('data-focused');
      });
    });
  }

  /**
   * Enhanced hover effects
   */
  setupHoverEffects(menu) {
    const sublinks = menu.querySelectorAll('.mega-menu__sublink');
    
    sublinks.forEach((link) => {
      link.addEventListener('mouseenter', function() {
        this.classList.add('is-hovering');
      });

      link.addEventListener('mouseleave', function() {
        this.classList.remove('is-hovering');
      });
    });

    // Promo card parallax effect
    const promoCards = menu.querySelectorAll('.mega-menu-promo__card--link');
    
    promoCards.forEach((card) => {
      card.addEventListener('mousemove', (e) => {
        const rect = card.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const y = e.clientY - rect.top;
        
        const centerX = rect.width / 2;
        const centerY = rect.height / 2;
        
        const deltaX = (x - centerX) / centerX;
        const deltaY = (y - centerY) / centerY;
        
        const image = card.querySelector('.mega-menu-promo__image');
        if (image) {
          image.style.transform = `scale(1.08) translate(${deltaX * 5}px, ${deltaY * 5}px)`;
        }
      });

      card.addEventListener('mouseleave', () => {
        const image = card.querySelector('.mega-menu-promo__image');
        if (image) {
          image.style.transform = 'scale(1) translate(0, 0)';
        }
      });
    });
  }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    new MegaMenuProfessional();
  });
} else {
  new MegaMenuProfessional();
}

// Re-initialize when megamenu is dynamically added
const observer = new MutationObserver((mutations) => {
  mutations.forEach((mutation) => {
    mutation.addedNodes.forEach((node) => {
      if (node.nodeType === 1 && (node.classList?.contains('mega-menu--professional') || node.querySelector?.('.mega-menu--professional'))) {
        new MegaMenuProfessional();
      }
    });
  });
});

observer.observe(document.body, {
  childList: true,
  subtree: true
});

// Export for use in other scripts
if (typeof module !== 'undefined' && module.exports) {
  module.exports = MegaMenuProfessional;
}
